import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcRouteV1FixSessionQueryServiceFixSessionGet } from '@interxfi/api-client/generated/hooks/exc-route-v1-fix-session-query-service-fix-session-get';
import {
  EntityActionMenu,
  type EntityActionMenuConfig,
  type EntityActionMenuItem,
} from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import ClearIcon from '@interxfi/design-system/icons/react/icon-clear';
import EditIcon from '@interxfi/design-system/icons/react/icon-edit';
import PowerIcon from '@interxfi/design-system/icons/react/icon-power';
import UndoIcon from '@interxfi/design-system/icons/react/icon-undo';
import { type ExcRouteV1FixSessionState, ExcRouteV1FixSessionStatus } from '@interxfi/types';
import { FixSessionConnectDialog } from '../../../../../../../../../../features/fix/session/fix-session-connect-dialog';
import { FixSessionDisconnectDialog } from '../../../../../../../../../../features/fix/session/fix-session-disconnect-dialog';
import { FixSessionEditDrawer } from '../../../../../../../../../../features/fix/session/fix-session-edit-drawer';
import { FixSessionEventDrawer, FixSessionEventSummary } from '../../../../../../../../../../features/fix/session/fix-session-event';
import { getFixSessionStateListItems } from '../../../../../../../../../../features/fix/session/fix-session-list-items';
import { getFixSessionDetailPath } from '../../../../../../../../../../features/fix/session/fix-session-routing';
import { FixSessionSequenceResetDialog } from '../../../../../../../../../../features/fix/session/fix-session-sequence-reset-dialog';
import { FixSessionStatusPill } from '../../../../../../../../../../features/fix/session/fix-session-status-pill';
import { FixSessionSummaryBar } from '../../../../../../../../../../features/fix/session/summary-bar/fix-session-summary-bar';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute(
  '/_app/_clearing-core/_clearing-required/clearing/operations/_operations/oms/fix-session/_fix-session/$sessionId',
)({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `oms-fix-session-${params.sessionId}`,
        label: 'FIX Session',
        to: getFixSessionDetailPath(params.sessionId),
        suffix: (
          <CopyButton content={params.sessionId} label="Copy FIX Session ID" tooltip={{ copy: params.sessionId, copied: 'Copied FIX Session ID' }} />
        ),
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('FIX Session'),
  component: FixSessionDetail,
});

function getListItems(data: ExcRouteV1FixSessionState | undefined) {
  const listItems = getFixSessionStateListItems(data);

  return {
    generalLeft: [
      listItems.statusPill,
      listItems.data.senderSeq,
      listItems.data.targetSeq,
      listItems.data.initiatorRunning,
      listItems.data.isLoggedOn,
    ] satisfies ReadonlyListItem[],
    generalRight: [
      listItems.sessionId,
      listItems.senderCompId,
      listItems.senderSubId,
      listItems.targetCompId,
      listItems.targetSubId,
    ] satisfies ReadonlyListItem[],
    config: [
      listItems.data.config.targetHost,
      listItems.data.config.targetPort,
      listItems.data.config.fixVersion,
      listItems.data.config.venueType,
      listItems.data.config.startTime,
      listItems.data.config.endTime,
      listItems.data.config.heartbeatInterval,
      listItems.data.config.reconnectInterval,
      listItems.data.config.socketTimeout,
      listItems.data.config.resetOnLogon,
    ] satisfies ReadonlyListItem[],
  };
}

function getMenuItems(sessionId: string, status: ExcRouteV1FixSessionStatus | undefined): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: FixSessionEventDrawer,
        props: { request: { sessionId: sessionId } },
      },
    },
    actions: [
      ...(status &&
      [
        ExcRouteV1FixSessionStatus.LoggedOn,
        ExcRouteV1FixSessionStatus.Connecting,
        ExcRouteV1FixSessionStatus.Disconnected,
        ExcRouteV1FixSessionStatus.Stopped,
      ].includes(status)
        ? [
            {
              id: 'edit',
              label: 'Edit FIX Session',
              icon: <EditIcon aria-hidden />,
              interaction: {
                type: 'drawer',
                drawer: {
                  Component: FixSessionEditDrawer,
                  props: { sessionId },
                },
              },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
            {
              id: 'resetSequence',
              label: 'Reset Sequence Numbers',
              icon: <UndoIcon aria-hidden />,
              interaction: {
                type: 'modal',
                modal: {
                  Component: FixSessionSequenceResetDialog,
                  props: { sessionId },
                },
              },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
          ]
        : []),
      ...(status && [ExcRouteV1FixSessionStatus.Stopped, ExcRouteV1FixSessionStatus.Connecting].includes(status)
        ? [
            {
              id: 'start',
              label: 'Start FIX Session',
              icon: <PowerIcon aria-hidden />,
              interaction: {
                type: 'modal',
                modal: {
                  Component: FixSessionConnectDialog,
                  props: { sessionId },
                },
              },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
          ]
        : []),
      ...(status &&
      [ExcRouteV1FixSessionStatus.LoggedOn, ExcRouteV1FixSessionStatus.Connecting, ExcRouteV1FixSessionStatus.Disconnected].includes(status)
        ? [
            {
              id: 'stop',
              label: 'Stop FIX Session',
              tone: 'danger-dk',
              icon: <ClearIcon aria-hidden />,
              interaction: {
                type: 'modal',
                modal: {
                  Component: FixSessionDisconnectDialog,
                  props: { sessionId },
                },
              },
              requiresWrite: true,
            } as const satisfies EntityActionMenuItem,
          ]
        : []),
    ],
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load FIX session', 'view this FIX session');

function FixSessionDetail() {
  const { sessionId } = Route.useParams();
  const request = { sessionId } as const;
  const { data, error, isPending } = useExcRouteV1FixSessionQueryServiceFixSessionGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const listItems = useMemo(() => getListItems(data?.fixSession), [data?.fixSession]);
  const menuItems = useMemo(() => getMenuItems(sessionId, data?.fixSession?.status), [sessionId, data?.fixSession?.status]);

  return (
    <PageLayout gap={DEFAULT_PAGE_LAYOUT_GAP} maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section>
        <VerticalStack gap={7}>
          <PageHeader>
            <HorizontalStack gap={2} wrap="nowrap">
              <FixSessionStatusPill isPending={showPending} size="large" status={data?.fixSession?.status} />
            </HorizontalStack>
            <EntityActionMenu {...menuItems} isPending={showPending} />
          </PageHeader>

          <FixSessionSummaryBar {...request} />

          <VerticalStack gap={4}>
            <Typography.Level3>Events</Typography.Level3>

            <FixSessionEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
          </VerticalStack>
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.generalLeft} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.generalRight} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Config</Typography.Level3>

          <AppReadonlyList isPending={showPending} items={listItems.config} />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
